import {faPlus} from '@fortawesome/pro-solid-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {Button, Empty, Modal} from 'antd';
import {UIEvent, useCallback, useRef, useState} from 'react';
import {v4 as uuidv4} from 'uuid';

import {Spinner} from '../../../../layout/spinner/spinner';
import {Locale} from '../../../../provider/locale/locale';
import {AnalyticsTarget, track} from '../../../../service/analytics/analytics';
import {
    useCreateWebhookMutation,
    useWebhookIntegrations,
} from '../../../../service/webhook-integration/use-webhook-integrations';
import {classNames} from '../../../../util/css';
import {onScrollInfiniteScrollContainer} from '../../../../util/infinite-scroll';
import * as integrationButtonStyles from '../integration-button/integration-button.scss';

import {WebhookIntegrationItem} from './webhook-integration-item';
import * as styles from './webhook-integration.scss';

type PropsType = {
    name: string;
    className?: string;
    webhookType: string;
};

export function WebhookIntegration(props: PropsType): JSX.Element {
    const {className, name, webhookType} = props;

    const fullClassName = classNames(integrationButtonStyles.integration_button, className);

    const [open, setOpen] = useState<boolean>(false);

    const webhooksContainerRef = useRef<HTMLDivElement | null>(null);

    const {
        data: webhookItems,
        isLoading: isLoadingList,
        refetch,
        loadMoreItems,
        removeItemFromList,
    } = useWebhookIntegrations({
        enabled: open,
        webhookType,
    });

    const {mutate: createWebhook, isLoading: isCreatingWebhook} = useCreateWebhookMutation();

    const handleCreateWebhook = useCallback(async () => {
        const webhookId = uuidv4();

        createWebhook(
            {
                webhookId,
                name: webhookId,
                webhookServiceCode: webhookType,
            },
            {
                onSuccess: () => {
                    refetch();
                    webhooksContainerRef?.current?.scrollTo({top: 0, behavior: 'smooth'});
                },
            }
        );
    }, [createWebhook, refetch, webhookType]);

    const closeModal = useCallback(() => {
        setOpen(false);
    }, []);

    const onWebhooksScroll = useCallback(
        (event: UIEvent) => {
            onScrollInfiniteScrollContainer(event, loadMoreItems);
        },
        [loadMoreItems]
    );

    return (
        <>
            <button
                className={fullClassName}
                onClick={() => {
                    track(AnalyticsTarget.Settings.Integration.Service, name);
                    setOpen(true);
                }}
                type="button"
            >
                <p className={integrationButtonStyles.integration_button__text}>{name}</p>
            </button>

            <Modal
                closable
                footer={null}
                maskClosable
                onCancel={closeModal}
                open={open}
                title={<Locale stringKey="INTEGRATIONS__WEBHOOK__TITLE" />}
                width={600}
            >
                <div
                    className={styles.WebhookIntegrationContent_webhooksContainer}
                    onScroll={onWebhooksScroll}
                    ref={webhooksContainerRef}
                >
                    <div
                        className={classNames(
                            styles.WebhookIntegrationContent_webhookItemContainer,
                            styles.WebhookIntegrationContent_webhookItemContainer__header
                        )}
                    >
                        <div>
                            <Locale stringKey="INTEGRATIONS__WEBHOOK__NAME" />
                        </div>
                        <div>
                            <Locale stringKey="INTEGRATIONS__WEBHOOK__URL" />
                        </div>
                        <div>
                            <Locale stringKey="INTEGRATIONS__WEBHOOK__STATUS" />
                        </div>
                        <div className={styles.WebhookIntegrationContent_webhookItemActions_wrapper}>
                            <Button
                                icon={<FontAwesomeIcon icon={faPlus} />}
                                loading={isCreatingWebhook}
                                onClick={handleCreateWebhook}
                                size="small"
                            >
                                <Locale stringKey="BUTTON__CREATE" />
                            </Button>
                        </div>
                    </div>

                    {isLoadingList && <Spinner position="absolute" />}
                    {webhookItems?.map((webhookItem) => {
                        return (
                            <WebhookIntegrationItem
                                key={webhookItem.id}
                                onWebhookDeleted={removeItemFromList}
                                webhookItem={webhookItem}
                            />
                        );
                    })}

                    {!webhookItems && <Empty className={styles.WebhookIntegrationContent_webhooksListEmpty} />}
                </div>
                <div className={styles.WebhookIntegrationContent_webhooksModalContainer} />
            </Modal>
        </>
    );
}
